import React, { useEffect, useState, useRef } from "react";

import { useDebouncedValue } from "../../../hooks/useDebouncedValues";
import { useUncontrolled } from "../../../hooks/useUncontrolled";
import { SearchIcon, XIcon } from "lucide-react";

import { IconButton, useTheme, SxProps } from "@mui/material";

import { Input } from "../Input/Input";

interface SearchProps {
  apiRef?: any;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  name?: string;
  defaultValue?: string;
  placeholder?: string;
  fullWidth?: boolean;
  debounce?: boolean;
  debounceDelay?: number;
  endOrdanment?: any;
  sx?: object;
}

//TODO: look into the proper search functionality
export const Search = ({
  apiRef,
  disabled = false,
  value,
  onChange = () => {},
  onClear = () => {},
  name,
  defaultValue,
  fullWidth = false,
  placeholder = "Search",
  debounce = true,
  debounceDelay = 500,
  sx = {},
}: SearchProps) => {
  const theme = useTheme();

  const [internalValue, internalOnChange] = useUncontrolled({
    value,
    defaultValue,
    finalValue: "",
  });

  // const [internalValue, internalOnChange] = useState(value)

  const [debounceValue] = useDebouncedValue(internalValue, debounceDelay);

  useEffect(() => {
    onDebounceChange();
  }, [internalValue, debounceValue]);

  const handleClear = () => {
    // TODO: TRE, get internal value to reset on clear
    internalOnChange("");
    onClear();

    if (apiRef) {
      apiRef.current.setQuickFilterValues([]);
    }
  };

  const handleChange = (value: string) => {
    internalOnChange(value);
    onChange(value);
    if (apiRef) {
      apiRef.current.setQuickFilterValues([value]);
    }
  };

  const onDebounceChange = () => {
    if (debounce) {
      onChange?.(debounceValue);
    } else {
      onChange?.(internalValue);
    }
  };

  const isClearable = internalValue?.length > 0;
  // TODO: not clearing input properly on X icon submit
  return (
    <Input
      defaultValue={internalValue}
      value={internalValue}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => handleChange(e)}
      fullWidth={fullWidth}
      // VRG-705 — the 24px magnifier is 1px taller than the field's 23px line, so it set the
      // field to 50 against every other field's 49 (same fix as the platform Search, VRG-703).
      // The negative half-pixel margins cancel that 1px of contribution; the glyph is unchanged.
      startAdornment={<SearchIcon strokeWidth={1} size={24} style={{ display: "block", margin: "-0.5px 0" }} />}
      endAdornment={
        <IconButton
          aria-label="clear search"
          onClick={() => handleClear()}
          sx={{
            display: isClearable ? "flex" : "none",
            // Small right gap so the clear "×" isn't flush against the field edge.
            // The negative margin offsets the IconButton's own 8px padding; an
            // `edge` prop would instead pull it flush.
            mr: -1.5,
            "&:hover": {
              // backgroundColor: theme.palette.primary[50],
              color: theme.palette.error.main,
            },
          }}
        >
          <XIcon strokeWidth={1} />
        </IconButton>
      }
      sx={{ minWidth: 300, ...sx }}
    />
  );
};
